import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Context
import { useContext, lazy, Suspense, useLayoutEffect } from "react";
import { AuthContext, AuthProvider } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";
import { usePartner, PartnerProvider } from "./context/PartnerContext";
import AdminProtectedRoute from "./routes/AdminProtectedRoute";

// Public Components
import Homepage from "./components/HomePage";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { ParentProvider } from "./context/ParentContext";
import ProtectedRoute from "./components/ProtectedRoute";
import TutorProtectedRoute from "./routes/TutorProtectedRoute";
import PartnerProtectedRoute from "./routes/PartnerProtectedRoute";
import PartnerRegistrationGate from "./routes/PartnerRegistrationGate";

// Childcare Components — lazily loaded; reduces initial JS bundle by ~40%
// ChildcareProtectedRoute is sync because it handles redirect logic before render
import ChildcareProtectedRoute from "./pages/childcare/_components/ProtectedRoute";
import ScrollToTop from "./ScrollToTop";

// Layouts
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminAuthLayout = lazy(() => import("./layouts/AdminAuthLayout"));
const PartnerDashboardLayout = lazy(() => import("./layouts/PartnerDashboardLayout"));
const TutorDashboardLayout = lazy(() => import("./layouts/TutorDashboardLayout"));
const ChildcareDashboardLayout = lazy(() => import("./layouts/childcareLayout/dashboardLayout"));
const ChildcareLayout = lazy(() => import("./layouts/childcareLayout"));
const PartnerAuthLayout = lazy(() => import("./layouts/PartnerAuthLauout"));
const NavbarPreview = lazy(() => import("./components/Navbar/NavbarPreview"));
const ChildcareFooter = lazy(() => import("./pages/childcare/_components/SimpleFooter"));
const Blogs = lazy(() => import("./components/Blogs"));
const BlogDetails = lazy(() => import("./components/BlogDetails"));
const Events = lazy(() => import("./features/public/Events"));
const EventDetails = lazy(() => import("./features/public/Events/EventDetails"));
const Classes = lazy(() => import("./components/Classes"));
const Tutorprofile = lazy(() => import("./components/Tutorprofile"));
const CenterDashboard = lazy(() => import("./components/CenterDashboard"));
const FAQ = lazy(() => import("./components/Faq"));
const Aboutus = lazy(() => import("./components/Aboutus"));
const Contactus = lazy(() => import("./components/Contactus"));
const RegistrationPage = lazy(() => import("./components/Registration"));
const ParentRegistration = lazy(() => import("./components/ParentRegistration"));
const TutorRegistration = lazy(() => import("./components/TutorRegistration"));
const TutorListing = lazy(() => import("./pages/tutors/TutorListing"));
const TutorDetails = lazy(() => import("./pages/tutors/TutorDetails"));
const SchoolDetails = lazy(() => import("./pages/partner/SchoolDetails"));
const Welcomepage = lazy(() => import("./components/Welcomepage"));
const WelcomeAboutus = lazy(() => import("./components/Aboutus/welcomeaboutus"));
const WelcomeContactus = lazy(() => import("./components/Contactus/welcomecontact"));
const WelcomeBlogs = lazy(() => import("./components/Blogs/welcomeblogs"));
const AddToCart = lazy(() => import("./components/AddToCart"));
const AdvertisingSubscription = lazy(() => import("./components/AdvertisingSubscriptions"));
const PlanDetails = lazy(() => import("./components/AdvertisingSubscriptions/components/PlanDetails"));
const PlanA = lazy(() => import("./components/AdvertisingSubscriptions/components/PlanA"));
const PlanB = lazy(() => import("./components/AdvertisingSubscriptions/components/PlanB"));
const PlanC = lazy(() => import("./components/AdvertisingSubscriptions/components/PlanC"));
const PlanD = lazy(() => import("./components/AdvertisingSubscriptions/components/PlanD"));
const PlanE = lazy(() => import("./components/AdvertisingSubscriptions/components/PlanE"));
const PlanF = lazy(() => import("./components/AdvertisingSubscriptions/components/PlanF"));
const PlanG = lazy(() => import("./components/AdvertisingSubscriptions/components/PlanG"));

// Auth Components
const PartnerLogin = lazy(() => import("./pages/registration/PartnerLogin"));
const PartnerForgotPassword = lazy(() => import("./pages/registration/PartnerForgotPassword"));
const PartnerNewPassword = lazy(() => import("./pages/registration/PartnerNewPassword"));
const PartnerVerification = lazy(() => import("./pages/registration/PartnerCodeVerification"));
const PartnerRegistration = lazy(() => import("./pages/registration/PartnerRegistration"));
const ParentAuth = lazy(() => import("./components/ParentAuth"));
const TutorLogin = lazy(() => import("./components/TutorLogin"));

const ForgotPasswordParent = lazy(() => import("./components/ForgotPasswordParent"));
const ResetPasswordParent = lazy(() => import("./components/ResetPasswordParent"));
const ForgotPasswordTutor = lazy(() => import("./components/ForgotPasswordTutor"));
const ResetPasswordTutor = lazy(() => import("./components/ResetPasswordTutor"));
const TutorVerifyCode = lazy(() => import("./components/TutorVerifyCode"));
const ResetStatus = lazy(() => import("./pages/admin/ResetStatus"));

// Chat
const Chat = lazy(() => import("./components/Chats"));

// Admin Components
const FAQAdminPanel = lazy(() => import("./pages/admin/FAQAdminPanel/FAQAdminPanel"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminForgotPassword = lazy(() => import("./pages/admin/AdminForgotPassword"));
const AdminNewPassword = lazy(() => import("./pages/admin/AdminNewPassword"));
const AdminVerification = lazy(() => import("./pages/admin/AdminVerification"));
const AddFaq = lazy(() => import("./pages/admin/AddFAQ/AddFAQ"));
const EditFaq = lazy(() => import("./pages/admin/EditFAQ/EditFAQ"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard/Dashboard"));
const AdminEmailTemplates = lazy(() => import("./pages/admin/AdminEmailTemplates"));
const CreateEmailTemplate = lazy(() => import("./pages/admin/AdminEmailTemplates/CreateEmailTemplate"));
const EditEmailTemplate = lazy(() => import("./pages/admin/AdminEmailTemplates/EditEmailTemplate"));
const AdminBlogs = lazy(() => import("./components/AdminBlogs/AdminBlogs"));
const AdminVerificationReq = lazy(() => import("./pages/admin/AdminverificationReq/AdminverificationReq"));
const ReviewsManagement = lazy(() => import("./pages/admin/ReviewsManagement"));
const AdminMessages = lazy(() => import("./pages/admin/Messages/Messages"));
const AdminBroadcast = lazy(() => import("./pages/admin/Broadcast/Broadcast"));

// Activities
const ActivityDetails = lazy(() => import("./pages/activities/ActivityDetails/index"));
const ActivitiesListing = lazy(() => import("./pages/activities"));

// Schools listing
const SchoolsListing = lazy(() => import("./pages/schools"));

// Community Discussion
const CommunityDiscussion = lazy(() => import("./pages/CommunityDiscussion"));
const PostDetail = lazy(() => import("./pages/CommunityDiscussion/PostDetail"));

// Partner Components
const EventsDashboard = lazy(() => import("./pages/partner/EventsDashboard/EventsDashboard"));
const AddEvent = lazy(() => import("./pages/partner/EventsDashboard/AddEvent"));
const ActivitiesDashboard = lazy(() => import("./pages/partner/ActivitiesDashboard/ActivitiesDashboard"));
const AddActivity = lazy(() => import("./pages/partner/ActivitiesDashboard/AddActivity"));
const MyProfile = lazy(() => import("./pages/partner/MyProfile"));
const PartnerCompleteProfile = lazy(() => import("./pages/partner/CompleteProfile"));
const EditProfile = lazy(() => import("./pages/partner/MyProfile/EditProfile"));
const ClassesDashboard = lazy(() => import("./pages/partner/ClassesDashboard/ClassesDashboard"));
const AddClass = lazy(() => import("./pages/partner/ClassesDashboard/AddClass"));
const TutorAddClass = lazy(() => import("./pages/tutors/tutorDashboard/ClassesDashboard/AddClass"));
const StaffDashboard = lazy(() => import("./pages/partner/StaffDashboard"));
const ReviewDashboard = lazy(() => import("./pages/partner/ReviewsDashboard"));
const PartnerDashboard = lazy(() => import("./pages/partner/Dashboard/Dashboard"));
const PartnerMessages = lazy(() => import("./pages/partner/Messages/messages"));
const PartnerBroadcast = lazy(() => import("./pages/partner/Broadcast"));
const PartnerHolidays = lazy(() => import("./pages/partner/Holidays"));
const Parents = lazy(() => import("./pages/partner/Parents"));
const ParentsDetails = lazy(() => import("./pages/partner/Parents/ParentDetails"));
const RefundRequests = lazy(() => import("./pages/partner/RefundRequests"));
const ChildReport = lazy(() => import("./pages/partner/ChildReport"));

const ReportIssue = lazy(() => import("./pages/partner/ReportIssue"));
const Enrollments = lazy(() => import("./pages/partner/Enrollments"));
const EnrollmentDetails = lazy(() => import("./pages/partner/Enrollments/EnrollmentDetails"));
const ChildDataEntry = lazy(() => import("./pages/partner/ChildDataEntry"));
const NotificationsPage = lazy(() => import("./pages/partner/NotificationsPage"));

const AdsSubscriptionPage = lazy(() => import("./pages/partner/AdsSubscriptionPage"));
const PartnerPayments = lazy(() => import("./pages/partner/Payments"));
const PartnerSettings = lazy(() => import("./pages/partner/Settings"));

// Parent Components
const Parentlogin = lazy(() => import("./components/ParentLogin"));
const ProviderLogin = lazy(() => import("./pages/ProviderLogin"));
const ParentDashboard = lazy(() => import("./pages/parent"));
const ParentProfilePublic = lazy(() => import("./pages/parent/ParentProfilePublic"));
const BrowseClasses = lazy(() => import("./pages/parent/browseClasses/BrowseClassess"));
const SavedClasses = lazy(() => import("./pages/savedClasses"));
const ParentRefundRequest = lazy(() => import("./pages/parent/components/ParentRefundRequest"));
const ParentReportIssue = lazy(() => import("./pages/parent/components/ParentReportIssue"));
const ParentPayments = lazy(() => import("./pages/parent/components/ParentPayments"));
const ParentProfile = lazy(() => import("./pages/parent/components/ParentProfile"));
const ParentSubscriptions = lazy(() => import("./pages/parent/components/ParentSubscriptions"));
const ParentMessages = lazy(() => import("./pages/parent/Messages/Messages"));
const ClassDetails = lazy(() => import("./components/Classes/components/Class-Details"));
const InstructorDetails = lazy(() => import("./pages/InstructorDetails"));

// Tutor Components
const TutorClassDetails = lazy(() => import("./pages/tutors/TutorClassDetails"));
const TutorRefundRequests = lazy(() => import("./pages/tutors/tutorDashboard/RefundRequests"));
const TutorEditProfile = lazy(() => import("./pages/tutors/tutorDashboard/MyProfile/EditProfile"));
const TutorChildReport = lazy(() => import("./pages/tutors/tutorDashboard/ChildReport"));
const TutorChildDataEntry = lazy(() => import("./pages/tutors/tutorDashboard/ChildDataEntry"));
const TutorReportIssue = lazy(() => import("./pages/tutors/tutorDashboard/ReportIssue"));
const TutorMessages = lazy(() => import("./pages/tutors/tutorDashboard/Messages/messages"));
const TutorHolidays = lazy(() => import("./pages/tutors/Holidays"));
const TutorSettings = lazy(() => import("./pages/tutors/Settings/settings"));
const TutorReviewDashboard = lazy(() => import("./pages/tutors/tutorDashboard/ReviewsDashboard"));
const TutorCompleteProfile = lazy(() => import("./pages/tutors/CompleteProfile"));
const TutorDashboardComponent = lazy(() => import("./pages/tutors/tutorDashboard/Dashboard/Dashboard"));
const TutorClassesDashboard = lazy(() => import("./pages/tutors/tutorDashboard/ClassesDashboard/ClassesDashboard"));
const TutorMyProfile = lazy(() => import("./pages/tutors/tutorDashboard/MyProfile"));
const TutorPayments = lazy(() => import("./pages/tutors/tutorDashboard/Payments"));
const TutorAttendanceDashboard = lazy(() => import("./pages/tutors/tutorDashboard/Attendance"));
const TutorActivitiesDashboard = lazy(() => import("./pages/tutors/tutorDashboard/ActivitiesDashboard/ActivitiesDashboard"));
const AddTutorActivity = lazy(() => import("./pages/tutors/tutorDashboard/ActivitiesDashboard/AddActivity"));
const TutorEnrollments = lazy(() => import("./pages/tutors/tutorDashboard/Enrollments"));
const TutorEnrollmentDetails = lazy(() => import("./pages/tutors/tutorDashboard/Enrollments/EnrollmentDetails"));
const TutorEventsDashboard = lazy(() => import("./pages/tutors/tutorDashboard/EventsDashboard/EventsDashboard"));
const AddTutorEvent = lazy(() => import("./pages/tutors/tutorDashboard/EventsDashboard/AddEvent"));

const TutorAuthLayout = lazy(() => import("./layouts/TutorAuthLayout"));

// Invited Signup
const SetPassword = lazy(() => import("./pages/invitedSignup/SetPassword"));
const CompleteProfile = lazy(() => import("./pages/invitedSignup/CompleteProfile"));
const PartnerInstructorCompleteProfile = lazy(() => import("./pages/invitedSignup/PartnerInstructorCompleteProfile"));

// Error Pages
const ServerError = lazy(() => import("./pages/errorPages/ServerError"));
const NotFound = lazy(() => import("./pages/errorPages/NotFound"));
const AccessDenied = lazy(() => import("./pages/errorPages/AccessDenied"));

// Platform Pages
const HowItWorks = lazy(() => import("./pages/platformPages/HowItWorks"));
const TermsAndConditions = lazy(() => import("./pages/platformPages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/platformPages/PrivacyPolicy"));
const RefundAndCancellation = lazy(() => import("./pages/platformPages/RefundAndCancellation"));
const ParentSetting = lazy(() => import("./pages/parent/components/ParentSetting"));
const PaymentOverview = lazy(() => import("./pages/admin/Payment-overview/PaymentOverview"));
const Roles = lazy(() => import("./pages/admin/Roles/roles"));
const Profile = lazy(() => import("./pages/admin/Profile/profile"));
const RssFeed = lazy(() => import("./components/RssFeed"));
const ChildcareLogin = lazy(() => import("./pages/childcare/Login/Login"));
const ChildcareRegistration = lazy(
  () => import("./pages/childcare/Registration/Registration"),
);
const CenterInformation = lazy(
  () => import("./pages/childcare/Registration/steps/CenterInformation"),
);
const AdminDetails = lazy(
  () => import("./pages/childcare/Registration/steps/AdminDetails"),
);
const DocumentsUpload = lazy(
  () => import("./pages/childcare/Registration/steps/DocumentsUpload"),
);
const LoginCredentials = lazy(
  () => import("./pages/childcare/Registration/steps/LoginCredentials"),
);
const ChildcareAdminProfile = lazy(() => import("./pages/childcare/Profile"));
const ChildcarePersonalProfile = lazy(
  () => import("./pages/childcare/PersonalProfile"),
);
const ChildcareChildcareProfile = lazy(
  () => import("./pages/childcare/ChildcareProfile"),
);
const ChildcareCompleteProfile = lazy(
  () => import("./pages/childcare/CompleteProfile"),
);
const Dashboard = lazy(() => import("./pages/childcare/Dashboard"));
const ChildRoster = lazy(() => import("./pages/childcare/ChildRoster"));
const ChildDetails = lazy(
  () => import("./pages/childcare/ChildRoster/ChildProfile"),
);
const AddNewChild = lazy(() => import("./pages/childcare/AddChild"));
const Educator = lazy(() => import("./pages/childcare/Educator"));
const AddNew = lazy(() => import("./pages/childcare/Educator/AddNew"));
const EducatorProfile = lazy(
  () => import("./pages/childcare/Educator/EducatorProfile"),
);
const Attendance = lazy(() => import("./pages/childcare/Attendance"));
const AttendanceDetail = lazy(
  () => import("./pages/childcare/Attendance/AttendanceDetail"),
);
const ChildcarePrograms = lazy(() => import("./pages/childcare/Programs"));
const ChildcareProgramDetails = lazy(
  () => import("./pages/childcare/Programs/ProgramDetails"),
);
const AddChildcareProgram = lazy(
  () => import("./pages/childcare/Programs/AddProgram"),
);
const Reports = lazy(() => import("./pages/childcare/Reports"));
const Settings = lazy(() => import("./pages/childcare/Settings/settings"));
const ChildcareSubAdminListing = lazy(
  () => import("./pages/childcare/ManageAdmins/ManageAdmins"),
);
const ChildcareListing = lazy(() => import("./pages/childcare/ChildcareList"));
const FacilityDetails = lazy(() => import("./pages/childcare/FacilityDetails"));
const EnrollmentSuccess = lazy(
  () => import("./pages/childcare/EnrollmentSuccess"),
);
const DailyActivities = lazy(() => import("./pages/childcare/DailyActivities"));
const DailyActivityForm = lazy(
  () => import("./pages/childcare/DailyActivities/DailyActivityForm"),
);
const DailyActivityDetail = lazy(
  () => import("./pages/childcare/DailyActivities/DailyActivityDetail"),
);
const IncidentReports = lazy(() => import("./pages/childcare/IncidentReports"));
const IncidentReportForm = lazy(
  () => import("./pages/childcare/IncidentReports/IncidentReportForm"),
);
const IncidentReportDetail = lazy(
  () => import("./pages/childcare/IncidentReports/IncidentReportDetail"),
);
const ParentNotes = lazy(() => import("./pages/childcare/ParentNotes"));
const ParentNoteForm = lazy(
  () => import("./pages/childcare/ParentNotes/ParentNoteForm"),
);
const ParentNoteDetail = lazy(
  () => import("./pages/childcare/ParentNotes/ParentNoteDetail"),
);
const ProgressReports = lazy(() => import("./pages/childcare/ProgressReports"));
const ProgressReportForm = lazy(
  () => import("./pages/childcare/ProgressReports/ProgressReportForm"),
);
const ProgressReportDetail = lazy(
  () => import("./pages/childcare/ProgressReports/ProgressReportDetail"),
);
const ChildcareMessages = lazy(
  () => import("./pages/childcare/Messages/Messages"),
);
const ChildcareBroadcast = lazy(() => import("./pages/childcare/Broadcast"));
const ChildcarePayments = lazy(
  () => import("./pages/childcare/Payments/index"),
);
const InvitationSetPassword = lazy(
  () => import("./pages/childcare/Login/InvitationSetPassword"),
);
const ChildcareRefundRequests = lazy(
  () => import("./pages/childcare/RefundRequests"),
);
const ChildcareReportIssue = lazy(
  () => import("./pages/childcare/ReportIssues"),
);
const AdminReportIssues = lazy(() => import("./pages/admin/ReportIssues"));
const AdminRefundRequests = lazy(() => import("./pages/admin/RefundRequests"));
const AdminCommunityReports = lazy(
  () => import("./pages/admin/CommunityReports"),
);
const AdminSupportInbox = lazy(() => import("./pages/admin/SupportInbox"));
const ReviewManagement = lazy(
  () => import("./pages/childcare/ReviewManagement/ReviewManagement"),
);
const SupportInbox = lazy(() => import("./pages/childcare/SupportInbox"));
const DeletionRequests = lazy(
  () => import("./pages/childcare/DeletionRequests"),
);
const SupportThread = lazy(() => import("./pages/SupportThread"));
const ReportTracker = lazy(() => import("./pages/ReportTracker"));
const TrackRefund = lazy(() => import("./pages/public/TrackRefund"));
// Main App Component - Wrapped with AuthProvider
// Layout route that provides a shared Suspense boundary for all childcare pages
const ChildcareSuspenseBoundary = () => (
  <Suspense
    fallback={
      <div
        style={{ display: "flex", justifyContent: "center", padding: "4rem" }}
      >
        Loading...
      </div>
    }
  >
    <Outlet />
  </Suspense>
);

const INSTRUCTOR_ROLES = ["partner_instructor", "instructor"];

const InstructorRestrictedRoute = () => {
  const { partner } = usePartner();
  if (INSTRUCTOR_ROLES.includes(partner?.role)) {
    return <Navigate to="/partner/home" replace />;
  }
  return <Outlet />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SocketProvider>
          <AppContent />
        </SocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Content component that uses auth context
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUserId } = useContext(AuthContext);

  useLayoutEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.key]);

  // Define paths where Navbar should be hidden
  const navbarHiddenPaths = [
    "/admin",
    "/partner",
    "/tutor/",
    "/welcomepage",
    "/welcomeaboutus",
    "/welcomecontact",
    "/welcomeblogs",
    "/navbar-preview",
  ];

  // Define paths where Footer should be hidden
  const footerHiddenPaths = [
    "/welcomepage",
    "/welcomeaboutus",
    "/welcomecontact",
    "/welcomeblogs",
  ];

  // ✅ Public childcare paths that should NOT have authentication
  const publicChildcarePaths = [
    "/childcare-facility-listing",
    "/facility-details",
  ];

  // ========== CHILDCARE ROUTE CONDITIONS ==========
  const isChildcareAuthPage =
    location.pathname === "/childcare/login" ||
    location.pathname === "/childcare/forgot-password" ||
    location.pathname === "/childcare/reset-password" ||
    location.pathname === "/childcare/set-password";

  const isChildcareRegistrationPage =
    location.pathname === "/childcare/registration" ||
    location.pathname.startsWith("/childcare/registration/");

  // ✅ Check if current path is a public childcare path
  const isPublicChildcarePath = publicChildcarePaths.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`),
  );

  // ✅ Protected pages are those under /childcare/ (with slash) that are not auth/registration/listing/public
  const isChildcareProtectedPage =
    (location.pathname.startsWith("/childcare/") ||
      location.pathname === "/childcare") &&
    !isChildcareAuthPage &&
    !isChildcareRegistrationPage &&
    !isPublicChildcarePath;

  // Tutor auth pages live under /tutor/ but should still show the public navbar
  const tutorAuthPaths = [
    "/tutor/login",
    "/tutor/forgot-password",
    "/tutor/reset-password",
    "/tutor/verify-code",
  ];
  const isTutorAuthPage = tutorAuthPaths.some((path) =>
    location.pathname.startsWith(path),
  );

  // ========== NAVBAR VISIBILITY ==========
  const hideNavbar =
    (navbarHiddenPaths.some((path) => location.pathname.startsWith(path)) &&
      !isTutorAuthPage) ||
    isChildcareProtectedPage;

  // ========== FOOTER VISIBILITY ==========
  // Facility listing is a public childcare page but uses the common footer (same as homepage etc.)
  const isChildcareFacilityListing =
    location.pathname === "/childcare-facility-listing";

  const showChildcareFooter =
    (isChildcareAuthPage ||
      isChildcareRegistrationPage ||
      isPublicChildcarePath) &&
    !isChildcareFacilityListing;

  const showCommonFooter =
    !hideNavbar &&
    !footerHiddenPaths.some((path) => location.pathname.startsWith(path)) &&
    !isChildcareProtectedPage &&
    !showChildcareFooter;

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
      />

      {!hideNavbar && <Navbar />}

      <ScrollToTop />
      <main>
        <Suspense
          fallback={
            <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
              Loading...
            </div>
          }
        >
        <Routes>
          {/* ========== PUBLIC ROUTES ========== */}
          <Route path="/" element={<Homepage />} />
          <Route
            path="/partner-registration"
            element={<PartnerRegistration />}
          />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogdetails/:slug" element={<BlogDetails />} />
          <Route path="/welcomepage" element={<Welcomepage />} />
          <Route path="/welcomeaboutus" element={<WelcomeAboutus />} />
          <Route path="/welcomecontact" element={<WelcomeContactus />} />
          <Route path="/welcomeblogs" element={<WelcomeBlogs />} />
          <Route path="/events" element={<Events />} />
          <Route path="/event-details" element={<EventDetails />} />
          <Route path="/classes" element={<Classes />} />
          <Route path="/tutorprofile" element={<Tutorprofile />} />
          <Route path="/dashboard" element={<CenterDashboard />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/aboutus" element={<Aboutus />} />
          <Route path="/rssfeed" element={<RssFeed />} />
          <Route path="/contactus" element={<Contactus />} />
          <Route path="/activity-details" element={<ActivityDetails />} />
          <Route path="/activities" element={<ActivitiesListing />} />
          <Route path="/navbar-preview" element={<NavbarPreview />} />
          <Route path="/schools" element={<SchoolsListing />} />
          <Route
            path="/community-discussion"
            element={<CommunityDiscussion />}
          />
          <Route
            path="/community-discussion/:postId"
            element={<PostDetail />}
          />
          <Route
            path="/support/thread/:token"
            element={
              <Suspense
                fallback={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "4rem",
                    }}
                  >
                    Loading…
                  </div>
                }
              >
                <SupportThread />
              </Suspense>
            }
          />
          <Route
            path="/track-report/:token"
            element={
              <Suspense
                fallback={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "4rem",
                    }}
                  >
                    Loading…
                  </div>
                }
              >
                <ReportTracker />
              </Suspense>
            }
          />
          <Route
            path="/track-refund/:token"
            element={
              <Suspense
                fallback={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "4rem",
                    }}
                  >
                    Loading…
                  </div>
                }
              >
                <TrackRefund />
              </Suspense>
            }
          />
          <Route
            path="/school-details/:registrationId"
            element={<SchoolDetails />}
          />
          <Route path="/tutors/all" element={<TutorListing />} />
          <Route path="/tutors/:tutorId" element={<TutorDetails />} />
          <Route
            path="/tutors/:tutorId/classes/:classId"
            element={<TutorClassDetails />}
          />
          <Route path="/tutors/verify-code" element={<TutorVerifyCode />} />
          <Route path="/parents/:id" element={<ParentProfilePublic />} />
          <Route
            path="/chat"
            element={<Chat currentUserId={currentUserId} />}
          />
          {/* ========== TUTOR ROUTES ========== */}
          <Route path="/tutor">
            {/* Auth Routes */}
            <Route element={<TutorAuthLayout />}>
              <Route path="login" element={<TutorLogin />} />
              <Route path="forgot-password" element={<ForgotPasswordTutor />} />
              <Route path="reset-password" element={<ResetPasswordTutor />} />
              <Route path="verify-code" element={<TutorVerifyCode />} />
              <Route
                path="reset-password/success"
                element={
                  <ResetStatus
                    status="success"
                    title="Password Updated Successfully"
                    message="Your password has been changed."
                    onButtonClick={() => navigate("/tutor/login")}
                  />
                }
              />
              <Route
                path="reset-password/error"
                element={
                  <ResetStatus
                    status="error"
                    title="Password Update Failed"
                    message="Something went wrong."
                    onButtonClick={() => navigate("/tutor/forgot-password")}
                  />
                }
              />
            </Route>

            {/* Dashboard Routes */}
            <Route element={<TutorDashboardLayout />}>
              <Route element={<TutorProtectedRoute />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<TutorDashboardComponent />} />
                <Route
                  path="complete-profile"
                  element={<TutorCompleteProfile />}
                />
                <Route path="profile" element={<TutorMyProfile />} />
                <Route path="edit-profile" element={<TutorEditProfile />} />
                <Route path="classes" element={<TutorClassesDashboard />} />
                <Route path="classes/add" element={<TutorAddClass />} />
                <Route path="classes/edit/:id" element={<TutorAddClass />} />
                <Route
                  path="reviews-dashboard"
                  element={<TutorReviewDashboard />}
                />
                <Route path="messages" element={<TutorMessages />} />
                <Route path="holidays" element={<TutorHolidays />} />
                <Route
                  path="refund-requests"
                  element={<TutorRefundRequests />}
                />
                <Route path="child-report" element={<TutorChildReport />} />
                <Route
                  path="child-data-entry"
                  element={<TutorChildDataEntry />}
                />
                <Route path="report-issue" element={<TutorReportIssue />} />
                <Route path="payments" element={<TutorPayments />} />
                <Route
                  path="attendance"
                  element={<TutorAttendanceDashboard />}
                />
                <Route path="activities" element={<TutorActivitiesDashboard />} />
                <Route path="activities/add" element={<AddTutorActivity />} />
                <Route
                  path="activities/edit/:id"
                  element={<AddTutorActivity />}
                />
                <Route path="enrollments" element={<TutorEnrollments />} />
                <Route
                  path="enrollments/:id"
                  element={<TutorEnrollmentDetails />}
                />
                <Route path="events" element={<TutorEventsDashboard />} />
                <Route path="events/add" element={<AddTutorEvent />} />
                <Route path="events/edit/:id" element={<AddTutorEvent />} />
                <Route path="settings" element={<TutorSettings />} />
              </Route>
            </Route>
          </Route>
          {/* ========== AUTH ROUTES (Legacy - Keep for compatibility) ========== */}
          <Route
            path="/tutors/reset-password"
            element={<ResetPasswordTutor />}
          />
          {/* Password Reset Status Routes */}
          <Route
            path="/parents/reset-password/success"
            element={
              <ResetStatus
                status="success"
                title="Password Updated Successfully"
                message="Your password has been changed."
                onButtonClick={() => navigate("/parentlogin")}
              />
            }
          />
          <Route
            path="/parents/reset-password/error"
            element={
              <ResetStatus
                status="error"
                title="Password Update Failed"
                message="Something went wrong."
                onButtonClick={() => navigate("/tutors/forgot-password")}
              />
            }
          />
          <Route
            path="/tutors/reset-password/success"
            element={
              <ResetStatus
                status="success"
                title="Password Updated Successfully"
                message="Your password has been changed."
                onButtonClick={() => navigate("/TutorLogin")}
              />
            }
          />
          <Route
            path="/tutors/reset-password/error"
            element={
              <ResetStatus
                status="error"
                title="Password Update Failed"
                message="Something went wrong."
                onButtonClick={() => navigate("/tutors/forgot-password")}
              />
            }
          />
          {/* ========== REGISTRATION ROUTES ========== */}
          <Route path="/registration" element={<RegistrationPage />} />
          <Route path="/parent-registration" element={<ParentRegistration />} />
          <Route path="/tutor-registration" element={<TutorRegistration />} />
          <Route path="/Parentlogin" element={<Parentlogin />} />
          <Route path="/login-options" element={<ProviderLogin />} />
          <Route
            path="/parents/forgot-password"
            element={<ForgotPasswordParent />}
          />
          <Route path="/parents/verify-code" element={<ParentAuth />} />
          <Route
            path="/parents/reset-password"
            element={<ResetPasswordParent />}
          />
          <Route path="/class-details" element={<ClassDetails />} />
          <Route path="/instructor-details" element={<InstructorDetails />} />
          {/* ========== INVITED SIGNUP ROUTES ========== */}
          <Route path="/set-password" element={<SetPassword />} />
          <Route
            path="/complete-profile/co-admin"
            element={<CompleteProfile />}
          />
          <Route
            path="/complete-profile/instructor"
            element={<PartnerInstructorCompleteProfile />}
          />
          {/* ========== ERROR PAGES ========== */}
          <Route path="/server-error" element={<ServerError />} />
          <Route path="/not-found" element={<NotFound />} />
          <Route path="/access-denied" element={<AccessDenied />} />
          {/* ========== PLATFORM PAGES ========== */}
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/refund-and-cancellation"
            element={<RefundAndCancellation />}
          />
          {/* ========== ADVERTISING SUBSCRIPTIONS ========== */}
          <Route
            path="/advertising-subscriptions"
            element={
              <PartnerProvider>
                <AdvertisingSubscription />
              </PartnerProvider>
            }
          />
          <Route
            path="/advertising-subscriptions/plan-a"
            element={
              <PartnerProvider>
                <PlanA />
              </PartnerProvider>
            }
          />
          <Route
            path="/advertising-subscriptions/plan-b"
            element={
              <PartnerProvider>
                <PlanB />
              </PartnerProvider>
            }
          />
          <Route
            path="/advertising-subscriptions/plan-c"
            element={
              <PartnerProvider>
                <PlanC />
              </PartnerProvider>
            }
          />
          <Route
            path="/advertising-subscriptions/plan-d"
            element={
              <PartnerProvider>
                <PlanD />
              </PartnerProvider>
            }
          />
          <Route
            path="/advertising-subscriptions/plan-e"
            element={
              <PartnerProvider>
                <PlanE />
              </PartnerProvider>
            }
          />
          <Route
            path="/advertising-subscriptions/plan-f"
            element={
              <PartnerProvider>
                <PlanF />
              </PartnerProvider>
            }
          />
          <Route
            path="/advertising-subscriptions/plan-g"
            element={
              <PartnerProvider>
                <PlanG />
              </PartnerProvider>
            }
          />
          <Route
            path="/cart"
            element={
              <PartnerProvider>
                <AddToCart />
              </PartnerProvider>
            }
          />
          {/* ========== ADMIN ROUTES ========== */}
          <Route path="/admin">
            <Route element={<AdminAuthLayout />}>
              <Route path="login" element={<AdminLogin />} />
              <Route path="forgot-password" element={<AdminForgotPassword />} />
              <Route path="reset-password" element={<AdminNewPassword />} />
              <Route path="verify-code" element={<AdminVerification />} />
              <Route
                path="reset-password/success"
                element={
                  <ResetStatus
                    status="success"
                    title="Password Updated Successfully"
                    message="Your password has been changed."
                    onButtonClick={() => navigate("/admin/login")}
                  />
                }
              />
              <Route
                path="reset-password/error"
                element={
                  <ResetStatus
                    status="error"
                    title="Password Update Failed"
                    message="Something went wrong."
                    onButtonClick={() => navigate("/admin/forgot-password")}
                  />
                }
              />
            </Route>

            <Route element={<AdminLayout />}>
              <Route element={<AdminProtectedRoute />}>
                <Route path="faqs" element={<FAQAdminPanel />} />
                <Route path="faqs/add" element={<AddFaq />} />
                <Route path="blogs" element={<AdminBlogs />} />
                <Route path="reviews" element={<ReviewsManagement />} />
                <Route path="payment-overview" element={<PaymentOverview />} />
                <Route path="roles" element={<Roles />} />
                <Route
                  path="verificationreq"
                  element={<AdminVerificationReq />}
                />
                <Route path="profile" element={<Profile />} />
                <Route path="faqs/edit/:id" element={<EditFaq />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="broadcast" element={<AdminBroadcast />} />
                <Route
                  path="bulk-messaging"
                  element={<Navigate to="/admin/broadcast" replace />}
                />
                <Route
                  path="refund-requests"
                  element={<AdminRefundRequests />}
                />
                <Route path="report-issues" element={<AdminReportIssues />} />
                <Route
                  path="community-reports"
                  element={<AdminCommunityReports />}
                />
                <Route path="support-inbox" element={<AdminSupportInbox />} />
                <Route
                  path="email-template"
                  element={<AdminEmailTemplates />}
                />
                <Route
                  path="email-template/create"
                  element={<CreateEmailTemplate />}
                />
                <Route
                  path="email-template/edit/:id"
                  element={<EditEmailTemplate />}
                />
              </Route>
            </Route>
          </Route>
          {/* ========== PARTNER ROUTES ========== */}
          <Route path="/partner">
            {/* Auth Routes */}
            <Route element={<PartnerAuthLayout />}>
              <Route path="login" element={<PartnerLogin />} />
              <Route
                path="forgot-password"
                element={<PartnerForgotPassword />}
              />
              <Route path="reset-password" element={<PartnerNewPassword />} />
              <Route path="verify-code" element={<PartnerVerification />} />
              <Route
                path="reset-password/success"
                element={
                  <ResetStatus
                    status="success"
                    title="Password Updated Successfully"
                    message="Your password has been changed."
                    onButtonClick={() => navigate("/partner/login")}
                  />
                }
              />
            </Route>

            {/* Dashboard Routes */}
            <Route element={<PartnerProtectedRoute />}>
              <Route element={<PartnerDashboardLayout />}>
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<PartnerDashboard />} />
                <Route
                  path="complete-profile"
                  element={<PartnerCompleteProfile />}
                />
                {/* Every other partner page requires registration to be complete —
                  shows guidance in place of the page instead of hitting APIs
                  that would fail with "No registration found". */}
                <Route element={<PartnerRegistrationGate />}>
                  <Route path="events" element={<EventsDashboard />} />
                  <Route path="events/add" element={<AddEvent />} />
                  <Route path="events/edit/:id" element={<AddEvent />} />
                  <Route path="activities" element={<ActivitiesDashboard />} />
                  <Route path="activities/add" element={<AddActivity />} />
                  <Route path="activities/edit/:id" element={<AddActivity />} />
                  <Route path="profile" element={<MyProfile />} />
                  <Route path="classes" element={<ClassesDashboard />} />
                  <Route path="classes/add" element={<AddClass />} />
                  <Route path="classes/edit/:id" element={<AddClass />} />
                  <Route path="all-staff" element={<StaffDashboard />} />
                  <Route
                    path="reviews-dashboard"
                    element={<ReviewDashboard />}
                  />
                  <Route path="messages" element={<PartnerMessages />} />
                  <Route path="broadcast" element={<PartnerBroadcast />} />
                  <Route path="holidays" element={<PartnerHolidays />} />
                  <Route path="all-parents" element={<Parents />} />
                  <Route path="all-parents/:id" element={<ParentsDetails />} />
                  {/* Routes restricted from instructors */}
                  <Route element={<InstructorRestrictedRoute />}>
                    <Route
                      path="refund-requests"
                      element={<RefundRequests />}
                    />
                    <Route path="report-issue" element={<ReportIssue />} />
                  </Route>
                  <Route path="edit-profile" element={<EditProfile />} />
                  <Route path="child-report" element={<ChildReport />} />
                  <Route path="enrollments" element={<Enrollments />} />
                  <Route
                    path="enrollments/:id"
                    element={<EnrollmentDetails />}
                  />
                  <Route path="child-data-entry" element={<ChildDataEntry />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route
                    path="subscription"
                    element={<AdsSubscriptionPage />}
                  />
                  <Route path="subscription/:id" element={<PlanDetails />} />
                  <Route path="payments" element={<PartnerPayments />} />
                  <Route path="settings" element={<PartnerSettings />} />
                </Route>
              </Route>
            </Route>
          </Route>
          {/* ========== PARENT ROUTES ========== */}
          <Route
            path="/parent/*"
            element={
              <ParentProvider>
                <ProtectedRoute>
                  <Routes>
                    <Route path="dashboard" element={<ParentDashboard />} />
                    <Route path="browse-classes" element={<BrowseClasses />} />
                    <Route path="saved-activities" element={<SavedClasses />} />
                    <Route path="profile" element={<ParentProfile />} />
                    <Route
                      path="refund-request"
                      element={<ParentRefundRequest />}
                    />
                    <Route
                      path="report-issue"
                      element={<ParentReportIssue />}
                    />
                    <Route path="payments" element={<ParentPayments />} />
                    <Route
                      path="subscriptions"
                      element={<ParentSubscriptions />}
                    />
                    <Route path="setting" element={<ParentSetting />} />
                    <Route path="messages" element={<ParentMessages />} />
                  </Routes>
                </ProtectedRoute>
              </ParentProvider>
            }
          />
          {/* ========== CHILDCARE ROUTES — Suspense boundary for lazy-loaded pages ========== */}
          <Route element={<ChildcareSuspenseBoundary />}>
            {/* ========== CHILDCARE PUBLIC ROUTES (No Authentication) ========== */}
            <Route
              path="/childcare-facility-listing"
              element={<ChildcareListing />}
            />
            <Route path="/facility-details/:id" element={<FacilityDetails />} />
            <Route
              path="/childcare/enrollment-success"
              element={<EnrollmentSuccess />}
            />

            {/* ========== CHILDCARE AUTH & REGISTRATION ROUTES ========== */}
            <Route element={<ChildcareLayout />}>
              <Route path="/childcare/login" element={<ChildcareLogin />} />
              <Route
                path="/childcare/registration"
                element={<ChildcareRegistration />}
              >
                <Route index element={<Navigate to="step1" replace />} />
                <Route path="step1" element={<CenterInformation />} />
                <Route path="step2" element={<AdminDetails />} />
                <Route path="step3" element={<DocumentsUpload />} />
                <Route path="step4" element={<LoginCredentials />} />
              </Route>
              <Route
                path="/childcare/set-password"
                element={<InvitationSetPassword />}
              />
            </Route>

            {/* ========== CHILDCARE PROTECTED DASHBOARD ROUTES ========== */}
            <Route
              path="/childcare"
              element={
                <ChildcareProtectedRoute
                  allowedUserTypes={[
                    "childcare_admin",
                    "childcare_co_admin",
                    "childcare_caregiver",
                    "childcare_tutor",
                  ]}
                >
                  <ChildcareDashboardLayout />
                </ChildcareProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<ChildcareAdminProfile />} />
              <Route
                path="edit_profile"
                element={<Navigate to="/childcare/personal-profile" replace />}
              />
              <Route
                path="facility-settings"
                element={<Navigate to="/childcare/childcare-profile" replace />}
              />
              <Route
                path="facility"
                element={<Navigate to="/childcare/childcare-profile" replace />}
              />
              <Route
                path="my-profile"
                element={<Navigate to="/childcare/personal-profile" replace />}
              />
              <Route
                path="personal-profile"
                element={<ChildcarePersonalProfile />}
              />
              <Route
                path="childcare-profile"
                element={<ChildcareChildcareProfile />}
              />
              <Route
                path="complete-profile"
                element={<ChildcareCompleteProfile />}
              />
              <Route path="class-roster" element={<ChildRoster />} />
              <Route
                path="/childcare/class-roster/child-details/:id"
                element={<ChildDetails />}
              />
              <Route
                path="class-roster/addNewChild"
                element={<AddNewChild />}
              />
              <Route path="staff-roster" element={<Educator />} />
              <Route path="staff-roster/addNew" element={<AddNew />} />
              <Route
                path="staff-roster/profile/:id"
                element={<EducatorProfile />}
              />
              <Route path="attendance" element={<Attendance />} />
              <Route path="attendance/:id" element={<AttendanceDetail />} />
              <Route path="programs" element={<ChildcarePrograms />} />
              <Route path="programs/add" element={<AddChildcareProgram />} />
              <Route path="program/:id" element={<ChildcareProgramDetails />} />
              <Route path="messages" element={<ChildcareMessages />} />
              <Route path="broadcast" element={<ChildcareBroadcast />} />
              <Route path="payments" element={<ChildcarePayments />} />
              <Route
                path="manage-admins"
                element={<ChildcareSubAdminListing />}
              />
              <Route path="reports" element={<Reports />} />
              <Route
                path="refund-request"
                element={<ChildcareRefundRequests />}
              />
              <Route path="report-issue" element={<ChildcareReportIssue />} />
              <Route path="settings" element={<Settings />} />
              <Route path="parent-notes" element={<ParentNotes />} />
              <Route path="parent-notes/new" element={<ParentNoteForm />} />
              <Route path="parent-notes/:id" element={<ParentNoteDetail />} />
              <Route path="progress-reports" element={<ProgressReports />} />
              <Route
                path="progress-reports/new"
                element={<ProgressReportForm />}
              />
              <Route
                path="progress-reports/:id"
                element={<ProgressReportDetail />}
              />
              <Route path="incident-reports" element={<IncidentReports />} />
              <Route
                path="incident-reports/new"
                element={<IncidentReportForm />}
              />
              <Route
                path="incident-reports/:id"
                element={<IncidentReportDetail />}
              />
              <Route path="reviews" element={<ReviewManagement />} />
              <Route path="support-inbox" element={<SupportInbox />} />
              <Route path="deletion-requests" element={<DeletionRequests />} />
              <Route path="daily-activities" element={<DailyActivities />} />
              <Route
                path="daily-activities/new"
                element={<DailyActivityForm />}
              />
              <Route
                path="daily-activities/:id"
                element={<DailyActivityDetail />}
              />
            </Route>
          </Route>{" "}
          {/* End ChildcareSuspenseBoundary */}
        </Routes>
        </Suspense>
      </main>

      {showChildcareFooter && <ChildcareFooter />}
      {showCommonFooter && <Footer />}
    </>
  );
}

export default App;
